import { useState, useEffect } from 'react';
import CarComponent from '../../components/CarComponent';
import { useTranslation } from '../../hooks/useTranslation';
import { useCurrency } from '../../contexts/CurrencyContext';

export default function CarDetailsPageClient({ carData }) {
    const { selectedCurrency, convertPrice } = useCurrency();
    const { t } = useTranslation();
    const [selectedImage, setSelectedImage] = useState(0);
    const [openFAQ, setOpenFAQ] = useState(null);
    const [isPopupOpen, setIsPopupOpen] = useState(false);
    const [popupImageIndex, setPopupImageIndex] = useState(0);

    const cars = carData.similarCars || [];
    const residentsData = t('details.documents.residents_details', { returnObjects: true });

    const residents = residentsData.map((item, index) => ({
        title: item.title,
    }));
    const touristData = t('details.documents.tourists_details', { returnObjects: true });

    const tourists = touristData.map((item, index) => ({
        title: item.title,
    }));
    const faqItems = t('details.faqs.details', { returnObjects: true });

    const faqs = faqItems.map((item, index) => ({
        q: item.q,
        a: item.a
    }));
    
    const images = [
        carData.detail.thumbnail,
        carData.detail.image2,
        carData.detail.image3,
        carData.detail.image4
    ].filter(Boolean);

    // Icon mapping for spec cards
    const specIcons = {
        year: '/images/icons/spec/year.png',
        seats: '/images/icons/spec/capacity.png',
        transmission: '/images/icons/spec/transmission.png',
        fuel_type: '/images/icons/spec/fuel.png',
        color: '/images/icons/spec/color.png',
        engine: '/images/icons/spec/engine.png',
        speed: '/images/icons/spec/milage.png'
    };

    // WhatsApp Click Handler
    const handleWhatsAppClick = () => {
        const phoneNumber = "971507946317"; // without +
        const message = `Hello, I am interested in the ${carData.detail.name}, Brand: ${carData.detail.brandName}, Color: ${carData.detail.color}`;
        const url = `https://wa.me/${phoneNumber}?text=${encodeURIComponent(message)}`;
        window.open(url, "_blank");
    };

    // Call Click Handler
    const handleCallClick = () => {
        window.location.href = `tel:+971507946317`;
    };

    // Image popup handlers
    const openImagePopup = (index) => {
        setPopupImageIndex(index);
        setIsPopupOpen(true);
    };

    const closeImagePopup = () => {
        setIsPopupOpen(false);
    };

    const goToPreviousImage = () => {
        setPopupImageIndex((prevIndex) => 
            prevIndex === 0 ? images.length - 1 : prevIndex - 1
        );
    };

    const goToNextImage = () => {
        setPopupImageIndex((prevIndex) => 
            prevIndex === images.length - 1 ? 0 : prevIndex + 1
        );
    };

    // Handle keyboard navigation
    useEffect(() => {
        const handleKeyDown = (e) => {
            if (!isPopupOpen) return;
            
            if (e.key === 'Escape') {
                closeImagePopup();
            } else if (e.key === 'ArrowLeft') {
                goToPreviousImage();
            } else if (e.key === 'ArrowRight') {
                goToNextImage();
            }
        };

        if (isPopupOpen) {
            document.addEventListener('keydown', handleKeyDown);
            document.body.style.overflow = 'hidden';
        } else {
            document.removeEventListener('keydown', handleKeyDown);
            document.body.style.overflow = 'unset';
        }

        return () => {
            document.removeEventListener('keydown', handleKeyDown);
            document.body.style.overflow = 'unset';
        };
    }, [isPopupOpen]);

    return (
        <div className="bg-[#1C1C1C] text-white min-h-screen">

            {/* ================= HERO SECTION ================= */}
            <div className="max-w-7xl mx-5 lg:mx-[8rem] pt-24 pb-16">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">

                    {/* LEFT: IMAGE + THUMBNAILS */}
                    <div className="flex flex-col">

                        {/* MAIN IMAGE - Fixed aspect ratio */}
                        <div className="aspect-[4/3] overflow-hidden rounded-lg">
                            <img
                                src={images[selectedImage]}
                                alt={carData.detail.name}
                                onClick={() => openImagePopup(selectedImage)}
                                className="w-full h-full object-cover cursor-pointer"
                            />
                        </div>

                        {/* THUMBNAILS */}
                        <div className="flex gap-3 mt-4">
                            {images.map((img, index) => (
                                <img
                                    key={index}
                                    src={img}
                                    onClick={() => setSelectedImage(index)}
                                    className={`w-20 h-18 object-cover cursor-pointer border-2 rounded transition
                                        ${selectedImage === index
                                            ? "border-blue-500"
                                            : "border-transparent opacity-60 hover:opacity-100"
                                        }`}
                                />
                            ))}
                        </div>
                    </div>

                    {/* RIGHT: DETAILS */}
                    <div className="flex flex-col">

                        {/* TOP CONTENT */}
                        <div className="flex-1">
                            <h1 className="text-3xl lg:text-4xl font-bold mb-3">
                                {carData.detail.name}
                            </h1>

                            <p className="text-gray-400 text-sm leading-relaxed mb-5">
                                {carData.detail.description}
                            </p>

                            {/* PRICE */}
                            <div className="mb-5">
                                <p className="text-sm text-gray-400">
                                    {t("details.price_per_day")}
                                </p>
                                <p className="text-3xl font-semibold">
                                    {selectedCurrency.symbol}{' '}
                                    {selectedCurrency.label === 'USD'
                                        ? carData.detail.pricePerDayUsd
                                        : carData.detail.pricePerDayAed}
                                </p>
                            </div>

                            {/* ACTION BUTTONS */}
                            <div className="flex gap-3 mb-6">
                                <button
                                    onClick={handleCallClick}
                                    className="flex-1 h-12 rounded-xl bg-blue-600 hover:bg-blue-700 transition font-medium"
                                >
                                    {t("callNow")}
                                </button>

                                <button
                                    onClick={handleWhatsAppClick}
                                    className="flex-1 h-12 rounded-xl bg-green-500 hover:bg-green-600 transition font-medium"
                                >
                                    {t("details.send_message")}
                                </button>
                            </div>

                            {/* SPECS - 2 columns on mobile, 3 on desktop */}
                            <div className="space-y-3">
                                {/* First Row */}
                                <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
                                    <SpecCard 
                                        icon={specIcons.year}
                                        title={t("details.year")} 
                                        value={carData.detail.year} 
                                    />
                                    <SpecCard 
                                        icon={specIcons.transmission}
                                        title={t("details.transmission")} 
                                        value={carData.detail.transmission} 
                                    />
                                    <SpecCard 
                                        icon={specIcons.fuel_type}
                                        title={t("details.fuel_type")} 
                                        value={carData.detail.fuel_type} 
                                        className="col-span-2 lg:col-span-1"
                                    />
                                </div>

                                {/* Second Row */}
                                <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
                                    <SpecCard 
                                        icon={specIcons.color}
                                        title={t("details.color")} 
                                        value={carData.detail.color} 
                                    />
                                    <SpecCard 
                                        icon={specIcons.speed}
                                        title="TOP SPEED"
                                        value="9-12 km/l" 
                                    />
                                    <SpecCard 
                                        icon={specIcons.seats}
                                        title={t("details.seats")} 
                                        value={carData.detail.seat_number} 
                                        className="col-span-2 lg:col-span-1"
                                    />
                                </div>

                                {/* Third Row */}
                                <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
                                    <SpecCard 
                                        icon={specIcons.transmission}
                                        title="BODY TYPE"
                                        value="Convertible" 
                                    />
                                    <SpecCard 
                                        icon={specIcons.engine}
                                        title={t("details.engine")} 
                                        value={carData.detail.engine}
                                        allowWrap={true}
                                        className="col-span-2 lg:col-span-1"
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* ================= IMAGE POPUP ================= */}
            {isPopupOpen && (
                <div className="fixed inset-0 bg-black bg-opacity-95 flex items-center justify-center z-50">
                    <button
                        onClick={closeImagePopup}
                        className="absolute top-4 right-4 text-white text-4xl hover:text-gray-300 w-12 h-12 flex items-center justify-center"
                    >
                        ×
                    </button>

                    <button
                        onClick={goToPreviousImage}
                        className="absolute left-4 text-white text-5xl hover:text-gray-300 w-12 h-12 flex items-center justify-center"
                    >
                        ‹
                    </button>

                    <img
                        src={images[popupImageIndex]}
                        className="max-w-[90vw] max-h-[90vh] object-contain"
                        alt="Full size preview"
                    />

                    <button
                        onClick={goToNextImage}
                        className="absolute right-4 text-white text-5xl hover:text-gray-300 w-12 h-12 flex items-center justify-center"
                    >
                        ›
                    </button>
                </div>
            )}

            {/* ================= DOCUMENTS & FAQ ================= */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 mx-4 lg:mx-[6rem] pb-16">

                {/* DOCUMENTS */}
                <div className="bg-[#252525] rounded-2xl p-6">
                    <h3 className="text-xl font-semibold mb-4">
                        {t("details.documents.title")}
                    </h3>

                    <h4 className="font-medium mb-2">
                        {t("details.documents.uae_residents")}
                    </h4>
                    <ul className="list-disc list-inside text-gray-400 mb-6 space-y-1">
                        {residents.map((item, i) => (
                            <li key={i}>{t(item.title)}</li>
                        ))}
                    </ul>

                    <h4 className="font-medium mb-2">
                        {t("details.documents.tourists")}
                    </h4>
                    <ul className="list-disc list-inside text-gray-400 space-y-1">
                        {tourists.map((item, i) => (
                            <li key={i}>{t(item.title)}</li>
                        ))}
                    </ul>
                </div>

                {/* FAQ */}
                <div className="bg-[#252525] rounded-2xl p-6">
                    <h3 className="text-xl font-semibold mb-4">
                        {t("details.faqs.title")}
                    </h3>

                    {faqs.map((faq, index) => (
                        <div key={index} className="border border-gray-700 rounded-lg mb-3">
                            <button
                                onClick={() => setOpenFAQ(openFAQ === index ? null : index)}
                                className="w-full flex justify-between items-center px-4 py-3 text-left hover:bg-gray-800/50 transition rounded-lg"
                            >
                                <span className="font-medium">{faq.q}</span>
                                <span className="text-2xl ml-2 flex-shrink-0">{openFAQ === index ? "−" : "+"}</span>
                            </button>

                            {openFAQ === index && (
                                <div className="px-4 pb-4 text-gray-400 text-sm">
                                    {faq.a}
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            </div>

            {/* ================= SIMILAR CARS ================= */}
            <div className="mx-4 lg:mx-[2rem] mb-10">
                <h3 className="text-lg text-gray-300 mb-4">
                    {t("extra.similar_cars")}
                </h3>

                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
                    {cars.map((car, index) => (
                        <CarComponent key={index} car={car} />
                    ))}
                </div>
            </div>

        </div>
    );
}

function SpecCard({ icon, title, value, allowWrap = false, className = "" }) {
    return (
        <div className={`flex items-center gap-3 px-3 py-3 ${className}`}>
            <div className="flex-shrink-0 w-8 h-8">
                <img src={icon} alt={title} className="w-full h-full object-contain filter brightness-0 invert" />
            </div>
            <div className="flex-1 min-w-0">
                <p className={`text-sm font-medium text-white leading-tight ${allowWrap ? 'break-words' : 'truncate'}`}>
                    {value || "-"}
                </p>
            </div>
        </div>
    );
}